import { Component, computed, DestroyRef, inject, input, linkedSignal, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent, ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';
import {
	getRolDescription,
	getRolName,
	getRolShort,
	InstellingBron,
	InstellingETLStatus,
	RCumLaudeAccount,
	RInstelling,
	RInstellingAdditionalObjectKey,
	RJobrunInstelling,
	RRol,
} from '@cumlaude/service-contract';
import { BehaviorSubject, combineLatestWith, distinctUntilChanged, filter, firstValueFrom, map, shareReplay, switchMap, timer } from 'rxjs';
import { RestService } from '@cumlaude/shared-services';
import { AsyncPipe, DatePipe } from '@angular/common';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { InstellingLabelsComponent } from '../../components/instelling-labels/instelling-labels.component';
import { LicentiesCardComponent } from './licenties-card.component';
import { ModulesCardComponent } from './modules-card.component';
import { IconComponent, type IconName, type ColorToken } from '@cumlaude/shared-components-icons';
import { accountLabelMetAfkorting, addDays, equalDates, getDatum, includesIgnoreCaseAndDiacritics, Option } from '@cumlaude/shared-utils';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '@cumlaude/shared-components-dialogs';
import { Dialog } from '@angular/cdk/dialog';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { doorlooptijd, doorlooptijdFromMs, EtlService } from '../../services/etl.service';
import { isUndefined } from 'lodash-es';
import { JobrunInstellingDetailsDialogComponent } from '../../dialogs/jobrun-instelling-details-dialog/jobrun-instelling-details-dialog.component';

@Component({
	selector: 'app-instelling-page',
	templateUrl: './instelling-page.component.html',
	styleUrl: './instelling-page.component.scss',
	imports: [
		AsyncPipe,
		ButtonComponent,
		InstellingLabelsComponent,
		LicentiesCardComponent,
		ModulesCardComponent,
		DatePipe,
		ConfirmDialogComponent,
		ToggleButtonsComponent,
		SingleSelectDropdownComponent,
		IconComponent,
	],
})
export class InstellingPageComponent implements OnInit {
	private readonly router = inject(Router);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly restService = inject(RestService);
	private readonly destroyRef = inject(DestroyRef);
	protected readonly dialog = inject(Dialog);
	protected readonly toastr = inject(ToastrService);
	protected readonly etlService = inject(EtlService);

	etlBezigStatussen: InstellingETLStatus[] = [InstellingETLStatus.GEPLAND, InstellingETLStatus.BEZIG];

	readonly ETL_ICON: Partial<Record<InstellingETLStatus, { name: IconName; color: ColorToken }>> = {
		[InstellingETLStatus.GEPLAND]: { name: 'gepland', color: 'primary-2' },
		[InstellingETLStatus.BEZIG]: { name: 'bezig', color: 'primary-2' },
		[InstellingETLStatus.GEFAALD]: { name: 'error', color: 'error-1' },
		[InstellingETLStatus.GESLAAGD]: { name: 'checkmark', color: 'positive-1' },
	};

	options: string[] = ['Bulk', 'Delta'];

	readonly selectedOption = signal('Delta');

	somtodayUrl: string;

	readonly id = input<number | undefined>(undefined);

	id$ = toObservable(this.id);

	readonly bestuur = input<number | undefined>(undefined);

	readonly account = input<string | undefined>(undefined);

	refreshInstelling$ = new BehaviorSubject<void>(undefined);

	instelling$ = this.refreshInstelling$.pipe(
		switchMap(() => timer(0, 60 * 1000)),
		switchMap(() =>
			this.id$.pipe(
				filter((id): id is number => id !== undefined),
				switchMap((id) => this.restService.getInstelling(id, [RInstellingAdditionalObjectKey.LICENTIES]))
			)
		),
		shareReplay(1)
	);

	readonly instelling = toSignal(this.instelling$);

	jobruns$ = toObservable(this.selectedOption).pipe(
		combineLatestWith(this.instelling$),
		switchMap(([type, instelling]) => this.restService.getJobrunInstellingen({ instelling: instelling, bulkload: type === 'Bulk', limit: 10 })),
		map((jobruns) =>
			jobruns.map((jobrun) => {
				let instellingETLStatus = jobrun.instellingETLStatus;
				if (!jobrun.totMet && addDays(new Date(), -1).getTime() > new Date(jobrun.vanaf!).getTime())
					instellingETLStatus = InstellingETLStatus.GEFAALD;

				let stapGefaald = jobrun.stapGefaald;
				if (stapGefaald === 'onbekend') stapGefaald = undefined;

				return {
					...jobrun,
					instellingETLStatus,
					stapGefaald,
				};
			})
		)
	);

	readonly jobruns = toSignal(this.jobruns$, { initialValue: [] });

	readonly avgDoorlooptijd = computed(() => {
		const jobruns = this.jobruns().filter((jobrun) => jobrun.instellingETLStatus === InstellingETLStatus.GESLAAGD);
		if (jobruns.length === 0) return '-';

		const totalDoorlooptijd = jobruns
			.map((jobrun) => new Date(jobrun.totMet!).getTime() - new Date(jobrun.vanaf!).getTime())
			.reduce((a, b) => a + b, 0);

		return doorlooptijdFromMs(Math.floor(totalDoorlooptijd / jobruns.length));
	});

	accountOptions$ = this.instelling$.pipe(
		distinctUntilChanged((a, b) => a.id === b.id),
		switchMap((instelling) => this.restService.getAccountsByInstelling(instelling)),
		map((accounts) => accounts.map(this.mapToOption)),
		shareReplay(1)
	);

	readonly accountOptions = toSignal(this.accountOptions$, { initialValue: [] });

	readonly accountZoekFilter = signal('');

	readonly filteredRollen = computed(() => {
		const zoekFilter = this.accountZoekFilter();

		return new Set(
			Object.values(RRol)
				.map((rol) => ({
					rol,
					rolDescription: getRolDescription(rol).toLowerCase(),
					rolName: getRolName(rol).toLowerCase(),
				}))
				.filter(
					({ rolDescription, rolName }) =>
						includesIgnoreCaseAndDiacritics(rolName, zoekFilter) || includesIgnoreCaseAndDiacritics(rolDescription, zoekFilter)
				)
				.map(({ rol }) => rol)
		);
	});

	readonly activeAccountOptions = computed(() => {
		const accountOptions = this.accountOptions();
		const zoekFilter = this.accountZoekFilter();
		const rollen = this.filteredRollen();

		return accountOptions.filter((option) => {
			if (zoekFilter.trim().length == 0) return true;

			const account = option.value;
			if (includesIgnoreCaseAndDiacritics(account.naam, zoekFilter)) return true;
			if (includesIgnoreCaseAndDiacritics(account.afkorting, zoekFilter)) return true;

			const zoekFilterNumber = Number(zoekFilter);
			if (zoekFilterNumber > 0 && Number(account.id) === zoekFilterNumber) return true;

			return account.rollen.some((rol) => rollen.has(rol));
		});
	});

	readonly selectedAccount: WritableSignal<RCumLaudeAccount | undefined> = signal(undefined);

	readonly selectedAccountOption = computed(() => {
		const selectedAccount = this.selectedAccount();
		return this.accountOptions().find((option) => option.value === selectedAccount);
	});

	argoWorkflowsAvailable$ = this.restService.isArgoWorkflowsAvailable();

	readonly argoWorkflowsAvailable = toSignal(this.argoWorkflowsAvailable$);

	readonly templateName = toSignal(
		this.argoWorkflowsAvailable$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.instelling$),
			switchMap((instelling) =>
				this.restService
					.getWorkflowTemplates()
					.pipe(
						map((templates) =>
							templates
								.map((template) => template.metadata?.name)
								.find((template) => template?.includes(`${instelling.bron.toLowerCase()}-job`))
						)
					)
			)
		)
	);

	refreshWorkflows$ = new BehaviorSubject<void>(undefined);

	readonly runningWorkflows = toSignal(
		this.argoWorkflowsAvailable$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.refreshWorkflows$),
			switchMap(() => timer(0, 60 * 1000)),
			switchMap(() => this.restService.getWorkflows()),
			combineLatestWith(this.instelling$),
			map(([workflows, instelling]) => {
				const instellingETLStatus = instelling.instellingETLStatus;
				if (instellingETLStatus && [InstellingETLStatus.BEZIG, InstellingETLStatus.GEPLAND].includes(instellingETLStatus)) return true;

				return workflows
					.filter((workflow) => workflow.status.phase === 'Running')
					.map((workflow) => workflow.spec?.arguments?.parameters)
					.filter((parameters) => !isUndefined(parameters))
					.some((parameters) =>
						parameters.some((parameter) => parameter.name === 'env-inst-pk-keys' && parameter.value!.split(';').includes(instelling.id!))
					);
			})
		),
		{
			initialValue: false,
		}
	);

	readonly handmatigeETLGestart = linkedSignal(() => this.runningWorkflows());

	readonly handmatigeDeltaVerwerkingBeschikbaar = computed(() => {
		if (!this.argoWorkflowsAvailable()) return false;

		const instelling = this.instelling();
		if (!instelling) return false;

		if (!instelling.actief) return false;

		if (instelling.etlUitgeschakeld === 1) return false;

		const handmatigeETLGestart = this.handmatigeETLGestart();
		if (handmatigeETLGestart) return false;

		return !this.runningWorkflows();
	});

	readonly handmatigeBulkVerwerkingBeschikbaar = computed(() => {
		const instelling = this.instelling();
		if (!instelling) return false;

		if (!instelling.actief) return false;

		return instelling.etlUitgeschakeld === 0;
	});

	format = 'dd-MM-yy HH:mm';

	@ViewChild('confirmInstellingVerwijderen')
	confirmInstellingVerwijderen!: TemplateRef<{ data: { naam: string } }>;

	@ViewChild('confirmInstellingHeractiveren')
	confirmInstellingHeractiveren!: TemplateRef<{ data: { naam: string; bron: InstellingBron } }>;

	@ViewChild('confirmInstellingDeactiveren')
	confirmInstellingDeactiveren!: TemplateRef<{ data: { naam: string; bron: InstellingBron } }>;

	@ViewChild('confirmInstellingBulkEnDelta')
	confirmInstellingBulkEnDelta!: TemplateRef<{}>;

	constructor() {
		this.somtodayUrl = this.envConfig.somtodayUrl!;
	}

	ngOnInit() {
		this.accountOptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((accountOptions) => {
			const accountId = this.account();
			const fromQuery = accountId ? accountOptions.find((option) => option.value.id === accountId) : undefined;
			const supportOption = accountOptions.find((option) => option.value.support);
			this.selectedAccount.set((fromQuery ?? supportOption)?.value);
		});
	}

	goBack() {
		if (this.bestuur()) {
			this.router.navigate(['bestuur', this.bestuur()]);
		} else if (this.account()) {
			this.router.navigate(['gebruikers']);
		} else {
			this.router.navigate(['instelling']);
		}
	}

	async activateDataverwerking(instelling: RInstelling) {
		const result = await firstValueFrom(
			this.restService.putInstelling({
				...instelling,
				etlUitgeschakeld: 0,
			})
		);

		if (result) {
			this.toastr.success(`Dataverwerking voor instelling ${instelling.naam} is geactiveerd.`);
			this.refreshInstelling$.next();
		} else this.toastr.error(`Activeren van dataverwerking voor instelling ${instelling.naam} is mislukt.`);
	}

	async deactivateDataverwerking(instelling: RInstelling) {
		const result = await firstValueFrom(
			this.restService.putInstelling({
				...instelling,
				etlUitgeschakeld: 1,
			})
		);

		if (result) {
			this.toastr.success(`Dataverwerking voor instelling ${instelling.naam} is gedeactiveerd.`);
			this.refreshInstelling$.next();
		} else this.toastr.error(`Deactiveren van dataverwerking voor instelling ${instelling.naam} is mislukt.`);
	}

	activateInstelling(instelling: RInstelling) {
		const dialogRef = this.dialog.open(this.confirmInstellingHeractiveren, {
			data: {
				naam: instelling.naam,
				bron: instelling.bron,
			},
		});
		dialogRef.closed.subscribe((result) => {
			if (result) {
				if (instelling.bron === InstellingBron.Somtoday) return;

				this.restService
					.putInstelling({
						...instelling,
						geldigTot: '9999-12-31',
					})
					.subscribe((result) => {
						if (result) {
							this.toastr.success(`Instelling ${instelling.naam} is geactiveerd.`);
							this.refreshInstelling$.next();
						} else this.toastr.error(`Activeren van instelling ${instelling.naam} is mislukt.`);
					});
			}
		});
	}

	deactivateInstelling(instelling: RInstelling) {
		const dialogRef = this.dialog.open(this.confirmInstellingDeactiveren, {
			data: {
				naam: instelling.naam,
				bron: instelling.bron,
			},
		});
		dialogRef.closed.subscribe((result) => {
			if (result) {
				if (instelling.bron === InstellingBron.Somtoday) return;

				this.restService
					.putInstelling({
						...instelling,
						geldigTot: getDatum(),
					})
					.subscribe((result) => {
						if (result) {
							this.toastr.success(`Instelling ${instelling.naam} is gedeactiveerd.`);
							this.refreshInstelling$.next();
						} else this.toastr.error(`Deactiveren van instelling ${instelling.naam} is mislukt.`);
					});
			}
		});
	}

	deleteInstelling(instelling: RInstelling) {
		const dialogRef = this.dialog.open(this.confirmInstellingVerwijderen, {
			data: { naam: instelling.naam },
		});
		dialogRef.closed.subscribe((result) => {
			if (result) {
				this.restService
					.putInstelling({
						...instelling,
						verwijderd: true,
					})
					.subscribe((result) => {
						if (result) {
							this.toastr.success(`Instelling ${instelling.naam} is verwijderd.`);
							this.goBack();
						} else this.toastr.error(`Verwijderen van instelling ${instelling.naam} is mislukt.`);
					});
			}
		});
	}

	magisterSettings() {
		this.router.navigate(['instelling', this.id(), 'settings']);
	}

	async startDeltaverwerking(instelling: RInstelling) {
		if (!this.handmatigeDeltaVerwerkingBeschikbaar()) return;

		if (instelling.nextETLBulk) {
			const dialogRef = this.dialog.open(this.confirmInstellingBulkEnDelta, {});
			const result = await firstValueFrom(dialogRef.closed);
			if (!result) return;
		}

		const templateName = this.templateName();
		if (!templateName) return;

		const workflowStarted = await this.etlService.startInstellingWorkflow(templateName, false, instelling);
		const etlType = instelling.nextETLBulk ? 'Bulkverwerking' : 'Deltaverwerking';
		if (workflowStarted) {
			this.toastr.success(`${etlType} voor instelling ${instelling.naam} is gestart.`);
			this.handmatigeETLGestart.set(true);
			setTimeout(() => {
				this.refreshInstelling$.next();
				this.refreshWorkflows$.next();
			}, 5 * 1000);
		} else {
			this.toastr.error(`Starten van ${etlType} voor instelling ${instelling.naam} is mislukt.`);
		}
	}

	async toggleBulkVerwerking(instelling: RInstelling) {
		if (!this.handmatigeBulkVerwerkingBeschikbaar()) return;

		const resultaat = await this.etlService.toggleBulkETL(instelling);
		if (resultaat) {
			this.toastr.success(`Instelling ${resultaat.naam} draait tijdens de volgende ETL ${resultaat.nextETLBulk ? '' : 'niet '}als bulk.`);
			this.refreshInstelling$.next();
		} else this.toastr.error(`Switchen van bulkverwerking voor instelling ${instelling.naam} is mislukt.`);
	}

	isDefaultStartDatum(datum: string): boolean {
		return equalDates('1900-01-01T00:00:00', datum);
	}

	mapToOption(account: RCumLaudeAccount): Option<RCumLaudeAccount> {
		return new Option<RCumLaudeAccount>(account, `${accountLabelMetAfkorting(account)} - ${getRollenShort(account.rollen)}`);
	}

	impersonate(account: RCumLaudeAccount, target: string) {
		const subject = `U/${account.id}`;
		window.open(`${this.envConfig.authUrl}/impersonate?subjectToImpersonate=${encodeURIComponent(subject)}`, target);
	}

	openDetailsDialog(jobrunInstelling: RJobrunInstelling) {
		this.dialog.open(JobrunInstellingDetailsDialogComponent, {
			data: { jobrunInstelling },
		});
	}

	protected readonly InstellingETLStatus = InstellingETLStatus;
	protected readonly InstellingBron = InstellingBron;
	protected readonly doorlooptijd = doorlooptijd;
}

function getRollenShort(rollen: RRol[]) {
	return rollen.map((rol: RRol) => getRolShort(rol)).join();
}
